import postRequest from 'utils/postRequest'
import getRequest from 'utils/getRequest'
import deleteRequest from 'utils/deleteRequest'
import filterUndefinedFromObject from 'utils/filterUndefinedFromObject'
import patchRequest from 'utils/patchRequest'

const path = '/api/eco-system'
const reassessmentPath = '/api/reassessment/sites'

export const requestCreateSiteEcoSystem = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/eco-systems` : `${path}/${siteId}`
  return postRequest(url)
}

export const requestDeleteSiteEcoSystem = (ecoSystemId, siteHistoryId) => {

  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/eco-systems/${ecoSystemId}` : `${path}/${ecoSystemId}`
  return deleteRequest(url)
}

export const requestGetSiteEcosystems = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/eco-systems` : `${path}/${siteId}`
  return getRequest(url)
}

export const requestGetAllEcosystems = () => {
  return getRequest(`${path}?select=name`)
}

export const requestUpdateSiteEcoSystem = (ecoSystemId, siteHistoryId, {
  number,
  name,
  EcoSystemRedListId,
  globalExtent,
  extentAtSiteMin,
  extentAtSiteBest,
  extentAtSiteMax,
  assessmentDate,
  dataSource,
  triggerReasonId,
  triggerReasonText
}) => {

  const requestBody = filterUndefinedFromObject({
    number,
    name,
    EcoSystemRedListId,
    globalExtent,
    extentAtSiteMin,
    extentAtSiteBest,
    extentAtSiteMax,
    assessmentDate,
    dataSource,
    triggerReasonId,
    triggerReasonText
  })

  if (siteHistoryId){
    return patchRequest(`${reassessmentPath}/${siteHistoryId}/eco-systems/${ecoSystemId}`, requestBody)
  }
  return patchRequest(`${path}/${ecoSystemId}`, requestBody)
}

export const confirmEcoSystem = (ecoSystemId, isApproved) => {
  return postRequest(`${path}/${ecoSystemId}/confirm`, isApproved)
}

export const cancelEcosystemReassessmentRequest = ({ siteId, ecosystemId, isCancelled }) => {
  return postRequest(`${reassessmentPath}/${siteId}/eco-systems/${ecosystemId}/cancel`, { isCancelled: isCancelled })
}

const buildEcosystemFilesUrl = (siteId, siteHistoryId, suffix = '') => {
  if (!siteId && !siteHistoryId) {
    throw new Error('buildEcosystemFilesUrl: either siteId or siteHistoryId is required')
  }

  if (siteHistoryId) {
    return `${reassessmentPath}/${siteHistoryId}/eco-systems/supporting-docs${suffix}`
  }

  if (!siteId) {
    throw new Error('buildEcosystemFilesUrl: siteId is required when siteHistoryId is not provided')
  }

  return `${path}/${siteId}/supporting-docs${suffix}`
}

export const requestGetEcosystemFiles = async (siteId, siteHistoryId) => {
  const url = buildEcosystemFilesUrl(siteId, siteHistoryId)
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })

    if (response.status === 404) {
      console.warn('[API][ecosystem-files][GET:missing-endpoint]', url)
      return { files: [] }
    }

    let data = null
    try {
      data = await response.json()
    } catch (error) {
      console.warn('[API][ecosystem-files][GET:json-parse-failed]', error)
    }

    if (!response.ok) {
      console.warn('[API][ecosystem-files][GET:failed]', response.status, response.statusText)
      return { files: [] }
    }

    return data ?? { files: [] }
  } catch (error) {
    console.warn('[API][ecosystem-files][GET:error]', error)
    return { files: [] }
  }
}

export const requestUpdateEcosystemFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = buildEcosystemFilesUrl(siteId, siteHistoryId)
  const method = siteHistoryId ? 'PATCH' : 'POST'

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (options.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress(event.loaded, event.total)
        }
      })
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch (error) {
          console.error('[API][ecosystem-files][UPLOAD:parse-failed]', error)
          resolve({})
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    xhr.open(method, url)
    xhr.setRequestHeader('Accept', 'application/json')
    if (options.file?.name) {
      xhr.setRequestHeader('X-File-Name', encodeURIComponent(options.file.name))
    }
    xhr.withCredentials = true
    xhr.send(formData)
  })
}

export const requestDeleteEcosystemFiles = async (siteId, siteHistoryId, fileId) => {
  if (!fileId) {
    throw new Error('requestDeleteEcosystemFiles: fileId is required')
  }

  const url = buildEcosystemFilesUrl(siteId, siteHistoryId, `/${fileId}`)
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json'
    }
  })

  if (response.status === 204) {
    return {}
  }

  let data = null
  try {
    data = await response.json()
  } catch (error) {
    if (response.ok) {
      return {}
    }
    console.warn('[API][ecosystem-files][DELETE:json-parse-failed]', error)
  }

  if (!response.ok) {
    throw new Error(`Failed to delete ecosystem file (${response.status})`)
  }

  return data ?? {}
}